import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Package, Box, Clock, CheckCircle, Truck, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePublicLang, LangToggle } from "@/hooks/usePublicLang";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_KEYS = ["received", "in_progress", "shipped", "delivered"];
const STATUS_ICONS = { received: Box, in_progress: Clock, shipped: CheckCircle, delivered: Truck };
const STATUS_COLORS = { received: "#78716c", in_progress: "#f59e0b", shipped: "#10b981", delivered: "#3b82f6" };

const PublicTrackingPage = () => {
  const { trackingNumber } = useParams();
  const { lang, setLang, t } = usePublicLang();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!!trackingNumber);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(trackingNumber || "");

  const fetchOrder = async (num) => {
    if (!num) return;
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/api/public/tracking/${num.trim().toUpperCase()}`);
      setOrder(res.data);
    } catch (e) { setError(t("tracking_not_found")); setOrder(null); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (trackingNumber) fetchOrder(trackingNumber); }, [trackingNumber]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      window.history.replaceState(null, "", `/tracking/${searchInput.trim().toUpperCase()}`);
      fetchOrder(searchInput);
    }
  };

  const statusLabel = (key) => t(`tracking_${key}`);
  const currentStepIdx = order ? STATUS_KEYS.indexOf(order.status) : -1;

  return (
    <div className="min-h-screen bg-stone-950 py-12 px-4" data-testid="tracking-page">
      <div className="fixed top-4 right-4 z-50"><LangToggle lang={lang} setLang={setLang} /></div>
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-10">
          <img src="/assets/logo-sally.png" alt="Sally Better" className="h-10 mx-auto mb-5" onError={(e) => e.target.style.display = 'none'} />
          <Package className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">{t("tracking_title")}</h1>
          <p className="text-stone-400">{t("tracking_subtitle")}</p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 mb-8">
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
            placeholder={t("tracking_search_placeholder")}
            className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 focus:border-indigo-500 font-mono" data-testid="tracking-search-input" />
          <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 px-6" data-testid="tracking-search-btn"><Search className="w-4 h-4" /></Button>
        </form>

        {loading && <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-stone-400 mx-auto" /></div>}
        {error && <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center"><p className="text-red-400">{error}</p></div>}

        {order && !loading && (
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
              <p className="text-stone-500 text-xs uppercase tracking-wider mb-1">{t("tracking_number")}</p>
              <p className="text-2xl font-mono font-bold text-white tracking-wider mb-2">{order.tracking_number}</p>
              <p className="text-stone-400">{order.client_name}</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="tracking-timeline">
              <div className="relative">
                {STATUS_KEYS.map((key, idx) => {
                  const StepIcon = STATUS_ICONS[key];
                  const color = STATUS_COLORS[key];
                  const isCompleted = idx <= currentStepIdx;
                  const isCurrent = idx === currentStepIdx;
                  const historyEntry = (order.status_history || []).find(h => h.status === key);

                  return (
                    <div key={key} className="flex gap-4 relative" data-testid={`tracking-step-${key}`}>
                      {idx < STATUS_KEYS.length - 1 && (
                        <div className="absolute left-[19px] top-[40px] w-[2px] h-[calc(100%-16px)]"
                          style={{ backgroundColor: isCompleted && idx < currentStepIdx ? color : "#3f3f46" }} />
                      )}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isCurrent ? 'ring-4 ring-opacity-30' : ''}`}
                        style={{ backgroundColor: isCompleted ? color : "#27272a", ringColor: isCurrent ? color : "transparent" }}>
                        <StepIcon className="w-5 h-5" style={{ color: isCompleted ? "white" : "#71717a" }} />
                      </div>
                      <div className={`pb-8 ${idx === STATUS_KEYS.length - 1 ? 'pb-0' : ''}`}>
                        <p className={`font-semibold ${isCompleted ? 'text-white' : 'text-stone-600'}`}>
                          {statusLabel(key)}
                          {isCurrent && <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}30`, color }}>{t("tracking_current")}</span>}
                        </p>
                        {historyEntry && (
                          <p className="text-stone-500 text-sm mt-1">
                            {new Date(historyEntry.date).toLocaleDateString()} {new Date(historyEntry.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {historyEntry.note && <span className="text-stone-400"> - {historyEntry.note}</span>}
                          </p>
                        )}
                        {key === "shipped" && order.carrier_tracking && isCompleted && (
                          <div className="mt-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                            <p className="text-amber-400 text-xs mb-1">{t("tracking_carrier")}</p>
                            <p className="text-white font-mono font-bold tracking-wider">{order.carrier_tracking}</p>
                            <a href={order.carrier_tracking.startsWith("1Z") ? `https://www.ups.com/track?tracknum=${order.carrier_tracking}` : order.carrier_tracking.length === 22 || order.carrier_tracking.length === 12 || order.carrier_tracking.length === 15 ? `https://www.fedex.com/fedextrack/?trknbr=${order.carrier_tracking}` : `https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.carrier_tracking}`}
                              target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-sm text-amber-400 underline hover:text-amber-300" data-testid="carrier-tracking-link">
                              {t("tracking_track")} &rarr;
                            </a>
                          </div>
                        )}
                        {!historyEntry && !isCompleted && <p className="text-stone-600 text-sm mt-1">{t("tracking_pending")}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <p className="text-stone-500 text-xs uppercase tracking-wider mb-3">{t("tracking_order_details")}</p>
              {(order.items || []).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <span className="text-white">{item.description}</span>
                  <span className="text-stone-400 text-sm">x{item.qty}</span>
                </div>
              ))}
              <p className="text-stone-500 text-xs mt-3">{t("tracking_created")}: {new Date(order.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicTrackingPage;
